/**
 * Utility functions for timezone handling in settings
 */

type TimezoneOption = {
  value: string;
  label: string;
};

/**
 * Formats a timezone identifier to display format: "(UTC ±HH:MM) City Name"
 * Example: "Australia/Brisbane" -> "(UTC +10:00) Brisbane"
 */
export const formatTimezoneDisplay = (timezone: string): string => {
  try {
    const now = new Date();

    // Get the offset in minutes
    const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(
      now.toLocaleString('en-US', { timeZone: timezone })
    );
    const offsetMinutes = (tzDate.getTime() - utcDate.getTime()) / (1000 * 60);

    // Format offset as ±HH:MM
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetMins = Math.abs(offsetMinutes) % 60;
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const offsetStr = `UTC ${sign}${String(offsetHours).padStart(
      2,
      '0'
    )}:${String(offsetMins).padStart(2, '0')}`;

    // Extract city name from timezone identifier (e.g., "Australia/Brisbane" -> "Brisbane")
    const cityName = timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;

    return `(${offsetStr}) ${cityName}`;
  } catch {
    // Fallback if formatting fails
    console.error('Error formatting timezone:', timezone);
    return timezone;
  }
};

/**
 * Gets UTC offset in hours for a given timezone
 */
const getTimezoneOffset = (timezone: string): number => {
  try {
    const now = new Date();
    const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(
      now.toLocaleString('en-US', { timeZone: timezone })
    );
    return (tzDate.getTime() - utcDate.getTime()) / (1000 * 60 * 60);
  } catch {
    return 0;
  }
};

/**
 * Common timezones organized by region
 * Using IANA timezone identifiers
 */
const COMMON_TIMEZONES = [
  // Australia
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Darwin',
  'Australia/Hobart',

  // North America
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',

  // Europe
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Rome',
  'Europe/Madrid',
  'Europe/Amsterdam',
  'Europe/Stockholm',
  'Europe/Dublin',
  'Europe/Athens',
  'Europe/Moscow',

  // Asia
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Seoul',

  // Pacific
  'Pacific/Auckland',
  'Pacific/Honolulu',
  'Pacific/Fiji',

  // South America
  'America/Sao_Paulo',
  'America/Buenos_Aires',
  'America/Santiago',

  // Africa
  'Africa/Johannesburg',
  'Africa/Cairo',
];

/**
 * Generates timezone options for the select dropdown
 * Returns options sorted by UTC offset, then alphabetically
 * Optionally includes custom timezones if they're not in the common list
 */
export const getTimezoneOptions = (
  ...includeTimezones: string[]
): TimezoneOption[] => {
  const timezonesSet = new Set(COMMON_TIMEZONES);

  // Add any timezones that aren't in our common list
  includeTimezones.forEach((tz) => {
    if (tz && !timezonesSet.has(tz)) {
      timezonesSet.add(tz);
    }
  });

  const allTimezones = Array.from(timezonesSet);
  const options: TimezoneOption[] = allTimezones.map((tz) => ({
    value: tz,
    label: formatTimezoneDisplay(tz),
  }));

  // Sort by UTC offset (ascending), then by label alphabetically
  return options.sort((a, b) => {
    const offsetA = getTimezoneOffset(a.value);
    const offsetB = getTimezoneOffset(b.value);

    if (offsetA !== offsetB) {
      return offsetA - offsetB;
    }

    return a.label.localeCompare(b.label);
  });
};
